import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'
import { Platform } from 'react-native'

export const useCaptchaV3 = ({ onCaptchaChange }: { onCaptchaChange: (captcha: string) => void }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { executeRecaptchaV3 } = useContext(CaptchaContext)

  const handleVerifyRecaptcha = useCallback(async () => {
    if (!executeRecaptchaV3) return

    setIsLoading(true)
    const token = await executeRecaptchaV3('token')
    setIsLoading(false)

    onCaptchaChange(token)
    setIsSuccess(true)
  }, [executeRecaptchaV3, onCaptchaChange])

  useEffect(() => {
    handleVerifyRecaptcha()
  }, [handleVerifyRecaptcha])

  return { isLoading, isSuccess }
}

export const useCaptchaV2 = () => useContext(CaptchaContext)

export const CaptchaV3Provider = ({
  children,
  executeRecaptchaV3,
  CaptchaV2Component,
}: PropsWithChildren<TCaptchaContext>): ReactNode => {
  const captchaContextValue = useMemo(
    () => ({ executeRecaptchaV3, CaptchaV2Component }),
    [executeRecaptchaV3, CaptchaV2Component],
  )

  if (Platform.OS !== 'web') {
    return children
  }

  return <CaptchaContext.Provider value={captchaContextValue}>{children}</CaptchaContext.Provider>
}

type TCaptchaContext = {
  executeRecaptchaV3?: (action: string) => Promise<string>
  CaptchaV2Component?: typeof ReCAPTCHA
}
const CaptchaContext = createContext<TCaptchaContext>({})
